import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp } from 'lucide-react';

import styles from './styles.module.css';

import {
  ACCEPT_ALL,
  applyConsent,
  buildConsentRecord,
  type ConsentCategories,
  hasGpcSignal,
  readConsent,
  REJECT_ALL,
  shouldShowBanner,
  writeConsent
} from '@/shared/analytics/consent';
import { track } from '@/shared/analytics/track';
import { fetchCookieBanner } from '@/shared/api/legal';
import PrivacyChoicesForm from '@/shared/components/ui/PrivacyChoicesForm';
import { LEGAL_ROUTES } from '@/shared/routes/paths';
import { useConsentStore } from '@/shared/stores/useConsentStore';

/**
 * Cookie consent banner. Renders on the first visit to any page (public or the
 * authenticated app — it is mounted once, in App). Honours the visitor's choice
 * for real by driving PostHog through the consent module, and re-appears when
 * the admin republishes the banner with new copy.
 *
 * A11y: role="dialog", labelled by its title, focus moved in on show and Tab
 * trapped inside. Deliberately NOT dismissible with Escape — a consent banner
 * that vanishes without a choice would imply consent it never got.
 *
 * "Manage preferences" only expands/collapses the category panel — it never
 * hides Accept All / Reject All, which stay live the whole time so a visitor
 * who opens the panel by mistake isn't forced to configure anything to leave.
 */
const CookieBanner: React.FC = () => {
  const reopenRequested = useConsentStore((s) => s.reopenRequested);
  const decidedThisSession = useConsentStore((s) => s.decidedThisSession);
  const markDecided = useConsentStore((s) => s.markDecided);

  const { data } = useQuery({
    queryKey: ['legal', 'cookieBanner'],
    queryFn: fetchCookieBanner,
    staleTime: 1000 * 60 * 10,
    retry: 1
  });

  // Read once on mount; a decision made this session is tracked in the store.
  const record = useMemo(() => readConsent(), []);
  const gpc = useMemo(() => hasGpcSignal(), []);

  const [managing, setManaging] = useState(false);
  // Seeded from the stored record (or defaulted) each time the banner opens —
  // see the effect below. Starting both at false here just avoids a flash of
  // stale state before that effect runs.
  const [analytics, setAnalytics] = useState(false);
  const [sale, setSale] = useState(false);

  const dialogRef = useRef<HTMLDivElement>(null);
  const acceptRef = useRef<HTMLButtonElement>(null);

  const config = data?.config ?? null;
  const version = data?.version ?? 0;
  const bannerEnabled = Boolean(data?.enabled && config);

  const visible =
    bannerEnabled &&
    !decidedThisSession &&
    (reopenRequested || shouldShowBanner(record, true, version));

  // Fire "shown" once per appearance. PostHog drops it while opted out, so in
  // practice it only lands for visitors who go on to opt in — by design.
  const shownRef = useRef(false);
  useEffect(() => {
    if (visible && !shownRef.current) {
      shownRef.current = true;
      track('cookie_consent_shown', {
        banner_version: version,
        gpc,
        reopened: reopenRequested
      });
    }
    if (!visible) shownRef.current = false;
  }, [visible, version, gpc, reopenRequested]);

  // Move focus into the banner when it appears.
  useEffect(() => {
    if (visible) acceptRef.current?.focus();
  }, [visible]);

  // Every time the banner (re)opens — including a reopen from "Your Privacy
  // Choices" after a prior decision — reset to a predictable state: the
  // Manage panel starts closed, and its toggles reflect the visitor's actual
  // stored choice rather than carrying over whatever was left from a previous
  // open in this same page session. Read fresh (not the mount-time `record`)
  // so this also picks up a decision made earlier in the same session.
  useEffect(() => {
    if (!visible) return;
    const stored = readConsent();
    setManaging(false);
    setAnalytics(stored?.categories.analytics ?? false);
    // GPC always overrides sale/sharing to off — see `decide` below, which
    // enforces the same rule when the decision is actually saved.
    setSale(gpc ? false : (stored?.categories.sale ?? false));
  }, [visible, gpc]);

  // Trap Tab within the banner while it's shown. Attached to the DOM node
  // rather than via a JSX handler so the container stays a plain region.
  useEffect(() => {
    if (!visible) return undefined;
    const el = dialogRef.current;
    if (!el) return undefined;

    const handler = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab') return;
      const focusable = el.querySelectorAll<HTMLElement>(
        'button, [href], input, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    el.addEventListener('keydown', handler);
    return () => el.removeEventListener('keydown', handler);
  }, [visible]);

  if (!visible || !config) return null;

  const decide = (categories: ConsentCategories): void => {
    // GPC always disables sale/sharing, even for an explicit "Accept all" —
    // the signal is an opt-out the visitor's browser is asserting on their
    // behalf, not a default that a single click should silently override.
    const finalCategories: ConsentCategories = gpc
      ? { ...categories, sale: false }
      : categories;

    // Apply to PostHog BEFORE tracking, so the opt-in is live when the decided
    // event fires (a rejecting visitor stays opted out and sends nothing).
    applyConsent(finalCategories);
    writeConsent(buildConsentRecord(finalCategories, version, gpc, Date.now()));
    track('cookie_consent_decided', {
      analytics: finalCategories.analytics,
      sale: finalCategories.sale,
      banner_version: version,
      gpc
    });
    markDecided();
  };

  return (
    <div
      ref={dialogRef}
      className={styles.banner}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-body"
    >
      <div className={styles.inner}>
        <div className={styles.copy}>
          <h2 id="cookie-banner-title" className={styles.title}>
            {config.title}
          </h2>
          <p id="cookie-banner-body" className={styles.body}>
            {config.body}{' '}
            <a
              className={styles.policyLink}
              href={LEGAL_ROUTES.PRIVACY}
              target="_blank"
              rel="noopener noreferrer"
            >
              {config.privacyPolicyLinkText}
            </a>
          </p>
          {gpc ? <p className={styles.gpc}>{config.gpcNoticeText}</p> : null}

          <div className={styles.categoriesWrap} data-open={managing}>
            <div className={styles.categoriesInner}>
              <div className={styles.categoriesBody}>
                <PrivacyChoicesForm
                  categories={config.categories}
                  analytics={analytics}
                  sale={sale}
                  gpc={gpc}
                  onToggleAnalytics={() => setAnalytics((v) => !v)}
                  onToggleSale={() => setSale((v) => !v)}
                />
                <button
                  type="button"
                  className={styles.saveButton}
                  onClick={() => decide({ necessary: true, analytics, sale })}
                >
                  {config.buttons.savePreferences}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.manageButton}
            aria-expanded={managing}
            onClick={() => setManaging((v) => !v)}
          >
            {managing ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {config.buttons.manage}
          </button>
          <button
            type="button"
            className={styles.secondary}
            onClick={() => decide(REJECT_ALL)}
          >
            {config.buttons.rejectAll}
          </button>
          <button
            ref={acceptRef}
            type="button"
            className={styles.primary}
            onClick={() => decide(ACCEPT_ALL)}
          >
            {config.buttons.acceptAll}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
